import { useEffect, useState } from "react";

import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";

import { useAutoRefresh } from "@/contexts/AutoRefreshContext";

/** How often the "updated x ago" label re-renders. */
const RELATIVE_TIME_TICK_MS = 15 * 1000;

const formatRelativeTime = (timestamp: number, now: number) => {
  const seconds = Math.max(0, Math.round((now - timestamp) / 1000));
  if (seconds < 45) {
    return "just now";
  }
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  return `${Math.round(minutes / 60)}h ago`;
};

const formatClockTime = (timestamp: number) =>
  new Date(timestamp).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

/**
 * Subtle "Updated 2m ago" label shown next to the app bar refresh control
 * whenever the current view refreshes its own data in the background.
 */
const AutoRefreshIndicator = () => {
  const { isActive, lastRefreshedAt, nextRefreshAt, isRefreshing, intervalMs } =
    useAutoRefresh();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }
    const intervalId = setInterval(
      () => setNow(Date.now()),
      RELATIVE_TIME_TICK_MS,
    );
    return () => clearInterval(intervalId);
  }, [isActive]);

  // Keep the label honest immediately after a refresh instead of waiting for
  // the next tick.
  useEffect(() => {
    setNow(Date.now());
  }, [lastRefreshedAt]);

  if (!isActive || lastRefreshedAt === null) {
    return null;
  }

  const intervalMinutes = Math.max(1, Math.round(intervalMs / 60000));
  const tooltip = isRefreshing
    ? "Refreshing now"
    : [
        `Last updated at ${formatClockTime(lastRefreshedAt)}`,
        nextRefreshAt ? `next at ${formatClockTime(nextRefreshAt)}` : null,
        `refreshes every ${intervalMinutes} min`,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          gap: 0.75,
          ml: 1,
          opacity: 0.65,
        }}
      >
        {isRefreshing && <CircularProgress size={12} color="inherit" />}
        <Typography
          variant="caption"
          aria-live="polite"
          sx={{ whiteSpace: "nowrap" }}
        >
          {isRefreshing
            ? "Refreshing…"
            : `Updated ${formatRelativeTime(lastRefreshedAt, now)}`}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default AutoRefreshIndicator;
